import { useCallback, useMemo, useState } from "react";
import { reconciliarOrdem, type OrdemDoMenu } from "@bookdental/schemas";
import type { NavGroup } from "./nav-items";
import { useAuth } from "@/lib/auth";
import { trpc } from "@/lib/trpc";

/**
 * Aplica a ordem do menu que a pessoa salvou e devolve como gravar uma nova.
 *
 * O rascunho local existe porque o menu não pode esperar o ida-e-volta do servidor pra mostrar o
 * item no lugar onde acabou de ser solto — arrastar e ver voltar é a sensação de que não funcionou.
 * Ele fica na frente do que veio em `me.current` até a invalidação trazer o mesmo valor de volta.
 */
export function useOrdemDoMenu(gruposVisiveis: NavGroup[]) {
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const [rascunho, setRascunho] = useState<OrdemDoMenu | null>(null);
  const salvar = trpc.me.setSidebarOrder.useMutation({
    onSuccess: () => utils.me.current.invalidate(),
  });

  const ordem = rascunho ?? user?.sidebarOrder ?? null;

  const grupos = useMemo(
    () =>
      reconciliarOrdem(gruposVisiveis, (g) => g.key, ordem?.grupos).map((grupo) => ({
        ...grupo,
        items: reconciliarOrdem(grupo.items, (i) => i.href, ordem?.itens?.[grupo.key]),
      })),
    [gruposVisiveis, ordem],
  );

  const gravar = useCallback(
    (nova: OrdemDoMenu) => {
      setRascunho(nova);
      salvar.mutate(nova);
    },
    [salvar],
  );

  const reordenarGrupos = useCallback(
    (chaves: string[]) => gravar({ grupos: chaves, itens: ordem?.itens ?? {} }),
    [gravar, ordem],
  );

  const reordenarItens = useCallback(
    (chaveDoGrupo: string, hrefs: string[]) =>
      gravar({
        // Grava os outros grupos junto: só o que está visível agora entra em `grupos`, e sem
        // preservar `itens` mexer no menu do laboratório apagaria o arranjo feito na clínica.
        grupos: ordem?.grupos?.length ? ordem.grupos : grupos.map((g) => g.key),
        itens: { ...(ordem?.itens ?? {}), [chaveDoGrupo]: hrefs },
      }),
    [gravar, ordem, grupos],
  );

  const restaurarPadrao = useCallback(() => {
    setRascunho({ grupos: [], itens: {} });
    salvar.mutate(null);
  }, [salvar]);

  const personalizado = Boolean(ordem && (ordem.grupos.length > 0 || Object.keys(ordem.itens).length > 0));

  return { grupos, reordenarGrupos, reordenarItens, restaurarPadrao, personalizado };
}
